"use client"

import type React from "react"
import { createContext, useContext, useState, useEffect } from "react"
import { auth, db } from "../config/firebase"
import {
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  type User,
} from "firebase/auth"
import { doc, getDoc } from "firebase/firestore"

interface AuthContextType {
  user: User | null
  loading: boolean
  apiEndpointConfigured: boolean
  apiEndpoint: string | null
  signIn: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  checkApiEndpoint: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [apiEndpointConfigured, setApiEndpointConfigured] = useState(false)
  const [apiEndpoint, setApiEndpoint] = useState<string | null>(null)

  useEffect(() => {
    console.log("AuthProvider: Setting up auth state listener")
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log("AuthProvider: Auth state changed", {
        userId: currentUser?.uid || "no user",
        email: currentUser?.email || "no email",
      })

      setUser(currentUser)

      if (currentUser) {
        // Check if the user has configured an API endpoint
        await checkApiEndpoint()
      } else {
        setApiEndpointConfigured(false)
        setApiEndpoint(null)
      }

      setLoading(false)
    })

    return unsubscribe
  }, [])

  async function checkApiEndpoint(): Promise<boolean> {
    if (!user) return false

    try {
      console.log("AuthProvider: Checking API endpoint for user:", user.uid)
      const userSettingsDoc = await getDoc(doc(db, "userSettings", user.uid))

      if (userSettingsDoc.exists() && userSettingsDoc.data().apiEndpoint) {
        const endpoint = userSettingsDoc.data().apiEndpoint
        console.log("AuthProvider: Found API endpoint in Firestore:", endpoint)

        // Validate the endpoint by making a test request
        try {
          console.log("AuthProvider: Validating endpoint:", endpoint)
          const response = await fetch(`${endpoint}assets`)

          if (response.ok) {
            // Only set as configured if the endpoint is valid
            console.log("AuthProvider: Endpoint is valid, updating state")
            setApiEndpoint(endpoint)
            setApiEndpointConfigured(true)
            return true
          } else {
            console.warn("AuthProvider: API endpoint exists but returned an error:", response.statusText)
            setApiEndpointConfigured(false)
            setApiEndpoint(null)
            return false
          }
        } catch (error) {
          console.warn("AuthProvider: API endpoint exists but could not be reached:", error)
          setApiEndpointConfigured(false)
          setApiEndpoint(null)
          return false
        }
      } else {
        console.log("AuthProvider: No API endpoint found for user:", user.uid)
        setApiEndpointConfigured(false)
        setApiEndpoint(null)
        return false
      }
    } catch (error) {
      console.error("AuthProvider: Error checking API endpoint:", error)
      setApiEndpointConfigured(false)
      setApiEndpoint(null)
      return false
    }
  }

  async function signIn(email: string, password: string) {
    try {
      console.log("AuthProvider: Signing in user:", email)
      await signInWithEmailAndPassword(auth, email, password)
      // After sign in, check API endpoint configuration
      if (auth.currentUser) {
        console.log("AuthProvider: User signed in, checking API endpoint for:", auth.currentUser.uid)
        await checkApiEndpoint()
      }
    } catch (error: any) {
      throw new Error(error.message)
    }
  }

  async function logout() {
    try {
      console.log("AuthProvider: Logging out user")
      await signOut(auth)
      setApiEndpointConfigured(false)
      setApiEndpoint(null)
    } catch (error: any) {
      throw new Error(error.message)
    }
  }

  async function resetPassword(email: string) {
    try {
      await sendPasswordResetEmail(auth, email)
    } catch (error: any) {
      throw new Error(error.message)
    }
  }

  const value = {
    user,
    loading,
    apiEndpointConfigured,
    apiEndpoint,
    signIn,
    logout,
    resetPassword,
    checkApiEndpoint,
  }

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>
}

